/**
 * Admin Property file (detail) page.
 *
 * One-screen view of everything tied to a property:
 *   - Address + nickname + owner contact
 *   - Sources (e.g. tank, bore)
 *   - All bookings & tests in reverse chronological order with deep links
 *   - Retest schedule + overdue flag
 *   - Timeline events (manual annotations)
 *   - Share invitations (Customer-controlled sharing)
 *   - Transfer history (ownership chain)
 *
 * Read-only for now — admin can drill into individual records via the
 * existing routes. Editing UIs already exist on the bookings/transfers/
 * timeline routers and will be linked into the next pass if needed.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Mail, Phone, Calendar, AlertTriangle, FileText, Share2, ArrowRightLeft, ClipboardList, Pencil, X, Building2 } from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BAND_CFG } from "@/components/passport/HealthScoreGauge";
import api from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";

export default function AdminPropertyDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => setMe(null));
  }, []);

  const reload = async () => {
    try {
      const { data } = await api.get(`/admin/properties/${id}`);
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load property");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const fmt = (d) => formatDateAU(d);
  const now = new Date();

  if (loading) return <PortalShell title="Property file"><div className="p-10 text-center text-slate-500">Loading…</div></PortalShell>;
  if (error || !data) return (
    <PortalShell title="Property file">
      <div className="p-10 text-center text-slate-500" data-testid="property-detail-error">{error || "Not found"}</div>
      <BackLink />
    </PortalShell>
  );

  const { property: p, tests, bookings, schedules, timeline, shares, transfers, test_count } = data;
  const latest = tests[0];
  const cfg = latest?.band ? BAND_CFG[latest.band] : null;
  const nextDue = schedules.find((s) => s.next_due_at);
  const overdue = nextDue && new Date(nextDue.next_due_at) < now;

  return (
    <PortalShell title="Property file" subtitle="AWARE Water Health Record — Admin">
      <BackLink />

      {/* Header */}
      <div data-testid="property-detail-header" className="rounded-3xl bg-white border border-slate-200 p-6 mb-5 relative">
        {(!me?.role || me.role === "admin") && (
          <button
            type="button"
            data-testid="property-detail-edit"
            onClick={() => setEditing(true)}
            className="absolute top-4 right-4 p-2 rounded-full text-slate-500 hover:text-cyan-700 hover:bg-cyan-50 transition"
            aria-label="Edit property details"
          >
            <Pencil className="w-4 h-4" />
          </button>
        )}
        <div className="flex items-start justify-between gap-4 flex-wrap pr-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 mb-1">Property file</div>
            <h1 className="font-display text-3xl font-bold text-slate-900">{p.nickname || p.address}</h1>
            {p.nickname && <div className="text-sm text-slate-600 mt-0.5">{p.address}</div>}
            <div className="mt-3 flex items-center gap-3 flex-wrap text-xs">
              <span className="inline-flex items-center gap-1 text-slate-700"><MapPin className="w-3.5 h-3.5" /> {p.property_type || "property"}</span>
              {p.owner_email && <span className="inline-flex items-center gap-1 text-slate-700"><Mail className="w-3.5 h-3.5" /> {p.owner_email}</span>}
              {p.owner_phone && <span className="inline-flex items-center gap-1 text-slate-700"><Phone className="w-3.5 h-3.5" /> {p.owner_phone}</span>}
            </div>
            {p.sources?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {p.sources.map((s) => (
                  <span key={s.id} className="px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700 capitalize">
                    {s.type.replace("_", " ")}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="text-right">
            {latest && cfg ? (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Latest score</div>
                <div className="flex items-center gap-2 justify-end mt-1">
                  <span className="font-display font-bold text-3xl text-slate-900">{latest.health_score}</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ background: cfg.color }}>
                    {cfg.label}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{fmt(latest.collected_at)}</div>
              </div>
            ) : (
              <div className="text-xs text-slate-400">No tests yet</div>
            )}
          </div>
        </div>

        {nextDue && (
          <div className={`mt-4 inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full ${
            overdue ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-cyan-50 text-cyan-800 border border-cyan-200"
          }`}>
            <Calendar className="w-3.5 h-3.5" />
            {overdue ? "Retest overdue" : "Next retest"}: {fmt(nextDue.next_due_at)}
            {overdue && <AlertTriangle className="w-3.5 h-3.5" />}
          </div>
        )}
      </div>

      {/* Two-column on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Tests + Bookings — main column */}
        <div className="lg:col-span-2 space-y-5">
          <Card title="Tests on file" icon={<ClipboardList className="w-4 h-4" />} count={test_count} testId="property-tests-card">
            {tests.length === 0 ? (
              <EmptyRow label="No tests recorded yet." />
            ) : (
              <ul className="divide-y divide-slate-100">
                {tests.map((t) => {
                  const tcfg = t.band ? BAND_CFG[t.band] : null;
                  return (
                    <li key={t.id} data-testid={`property-test-${t.id}`} className="py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <Link to={`/admin/lab/${t.id}`} className="font-semibold text-slate-900 hover:text-cyan-700 hover:underline truncate block">
                          {t.test_name || t.test_id} {t.sample_number && <span className="font-mono text-xs text-slate-500">· {t.sample_number}</span>}
                        </Link>
                        <div className="text-xs text-slate-500">{fmt(t.collected_at)} · status: <span className="capitalize">{(t.status || "—").replace(/_/g, " ")}</span></div>
                      </div>
                      {tcfg ? (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="font-bold text-slate-900">{t.health_score}</span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ background: tcfg.color }}>{tcfg.label}</span>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card title="Bookings" icon={<FileText className="w-4 h-4" />} count={bookings.length} testId="property-bookings-card">
            {bookings.length === 0 ? (
              <EmptyRow label="No bookings linked to this property yet." />
            ) : (
              <ul className="divide-y divide-slate-100">
                {bookings.map((b) => (
                  <li key={b.reference} data-testid={`property-booking-${b.reference}`} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{b.reference}</div>
                      <div className="text-xs text-slate-500">{b.test_name} · {fmt(b.created_at)} · status: <span className="capitalize">{(b.status || "—").replace(/_/g, " ")}</span></div>
                    </div>
                    <div className="text-xs font-semibold text-slate-700 shrink-0">${b.total}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Right column: timeline + shares + transfers */}
        <div className="space-y-5">
          <ManagedByCard property={p} onChanged={reload} />

          <Card title="Timeline" icon={<Calendar className="w-4 h-4" />} count={timeline.length} testId="property-timeline-card">
            {timeline.length === 0 ? (
              <EmptyRow label="No timeline events." />
            ) : (
              <ul className="space-y-3">
                {timeline.map((ev) => (
                  <li key={ev.id} className="text-xs">
                    <div className="font-semibold text-slate-800">{ev.title || ev.kind}</div>
                    {ev.note && <div className="text-slate-600 mt-0.5">{ev.note}</div>}
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">{fmt(ev.at)}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Sharing" icon={<Share2 className="w-4 h-4" />} count={shares.length} testId="property-shares-card">
            {shares.length === 0 ? (
              <EmptyRow label="Not shared." />
            ) : (
              <ul className="space-y-2">
                {shares.map((s) => (
                  <li key={s.id} className="text-xs">
                    <div className="font-semibold text-slate-800">{s.shared_with_email || s.recipient_email}</div>
                    <div className="text-slate-500">{s.scope || "view"} · {s.expires_at ? `expires ${fmt(s.expires_at)}` : "no expiry"}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Transfer history" icon={<ArrowRightLeft className="w-4 h-4" />} count={transfers.length} testId="property-transfers-card">
            {transfers.length === 0 ? (
              <EmptyRow label="No prior owners." />
            ) : (
              <ul className="space-y-2">
                {transfers.map((tr) => (
                  <li key={tr.id} className="text-xs">
                    <div className="font-semibold text-slate-800">{tr.from_email} → {tr.to_email}</div>
                    <div className="text-slate-500">{tr.status} · {fmt(tr.created_at)}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      {editing && (
        <EditPropertyModal
          property={p}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); reload(); }}
        />
      )}
    </PortalShell>
  );
}

function EditPropertyModal({ property, onClose, onSaved }) {
  const [form, setForm] = useState({
    nickname: property.nickname || "",
    property_type: property.property_type || "",
    owner_name: property.owner_name || "",
    owner_email: property.owner_email || "",
    owner_phone: property.owner_phone || "",
    internal_notes: property.internal_notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (form.owner_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.owner_email)) {
      toast.error("Invalid owner email");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/properties/${property.id}`, form);
      if (data.changes && data.changes.length) {
        toast.success(`Saved: ${data.changes.length} field${data.changes.length === 1 ? "" : "s"} updated`);
      } else {
        toast.info("No changes to save");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="edit-property-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-display text-lg font-bold text-slate-900">Edit property details</h2>
          <button
            type="button"
            data-testid="edit-property-close"
            onClick={onClose}
            className="p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <Row label="Property address (read-only)">
            <div data-testid="edit-property-address-readonly" className="text-sm text-slate-600 py-1">
              {property.address}
            </div>
            <p className="text-[10px] text-slate-400 mt-0.5">Change ownership via Transfers if this is wrong.</p>
          </Row>
          <Row label="Nickname">
            <Input
              data-testid="edit-property-nickname"
              value={form.nickname}
              onChange={(e) => setForm({ ...form, nickname: e.target.value })}
              placeholder="e.g. Mum's house"
            />
          </Row>
          <Row label="Property type">
            <select
              data-testid="edit-property-type"
              value={form.property_type}
              onChange={(e) => setForm({ ...form, property_type: e.target.value })}
              className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
            >
              <option value="">—</option>
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
              <option value="rural">Rural</option>
              <option value="industrial">Industrial</option>
            </select>
          </Row>
          <Row label="Owner name">
            <Input data-testid="edit-property-owner-name" value={form.owner_name}
                   onChange={(e) => setForm({ ...form, owner_name: e.target.value })} />
          </Row>
          <Row label="Owner email">
            <Input data-testid="edit-property-owner-email" type="email" value={form.owner_email}
                   onChange={(e) => setForm({ ...form, owner_email: e.target.value })} />
          </Row>
          <Row label="Owner phone">
            <Input data-testid="edit-property-owner-phone" value={form.owner_phone}
                   onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} />
          </Row>
          <Row label="Internal notes (not customer visible)">
            <Textarea
              data-testid="edit-property-internal-notes"
              value={form.internal_notes}
              onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
              rows={3}
            />
          </Row>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50">
          <Button data-testid="edit-property-cancel" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button data-testid="edit-property-save" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Card({ title, icon, count, children, testId }) {
  return (
    <section data-testid={testId} className="rounded-2xl bg-white border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 flex items-center gap-1.5">
          {icon} {title}
        </h2>
        {typeof count === "number" && <span className="text-xs font-bold text-slate-400 tabular-nums">{count}</span>}
      </div>
      {children}
    </section>
  );
}

function EmptyRow({ label }) {
  return <div className="text-xs text-slate-400 py-2">{label}</div>;
}

function BackLink() {
  return (
    <Link
      to="/admin/properties"
      data-testid="property-detail-back"
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-cyan-700 mb-3"
    >
      <ArrowLeft className="w-3.5 h-3.5" /> All properties
    </Link>
  );
}

/**
 * Phase 2L-A107 — admin "Managed by" assignment tile.
 *
 * Lists every active partner with `partner_type='property_manager'`
 * and lets the admin assign / unassign that partner to the current
 * property. The dropdown loads lazily on first render via
 * GET /admin/property-managers (cheap — partners are a small set).
 *
 * Audit trail: backend writes a `property_timeline_events` row on
 * every change, so each assignment/unassignment shows up in the
 * Timeline card directly below this tile.
 */
function ManagedByCard({ property, onChanged }) {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(property.managed_by_partner_id || "");

  useEffect(() => {
    setSelected(property.managed_by_partner_id || "");
  }, [property.managed_by_partner_id]);

  useEffect(() => {
    api
      .get("/admin/property-managers")
      .then(({ data }) => setManagers(data || []))
      .catch(() => setManagers([]))
      .finally(() => setLoading(false));
  }, []);

  const current = managers.find((m) => m.id === property.managed_by_partner_id);

  const save = async (nextId) => {
    if (saving) return;
    setSaving(true);
    try {
      if (nextId) {
        await api.post(`/admin/properties/${property.id}/assign-manager`, {
          partner_id: nextId,
        });
        toast.success("Property manager assigned");
      } else {
        await api.delete(`/admin/properties/${property.id}/manager`);
        toast.success("Property manager removed");
      }
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
      setSelected(property.managed_by_partner_id || "");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="managed-by-card" className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-cyan-50 flex items-center justify-center">
          <Building2 className="w-4 h-4 text-cyan-600" />
        </div>
        <h3 className="font-semibold text-slate-900 text-sm">Managed by</h3>
      </div>

      <div className="text-xs text-slate-500 mb-2">
        Current:{" "}
        <span data-testid="managed-by-current" className="font-semibold text-slate-700">
          {current
            ? (current.business_name || current.email)
            : property.managed_by_partner_id
              ? "unknown partner"
              : "— not assigned —"}
        </span>
      </div>

      {property.source === "property_manager" && (
        <div data-testid="property-source-pm-chip" className="mb-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-2.5 py-1">
          <Building2 className="w-3 h-3" />
          Source: Property Manager
          {property.created_at && (
            <span className="font-normal text-cyan-600 normal-case tracking-normal ml-1">
              · added {new Date(property.created_at).toLocaleDateString("en-AU")}
            </span>
          )}
        </div>
      )}

      <select
        data-testid="managed-by-select"
        disabled={loading || saving}
        value={selected}
        onChange={(e) => { setSelected(e.target.value); save(e.target.value); }}
        className="w-full text-sm rounded-lg border border-slate-300 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-200"
      >
        <option value="">— Unassigned —</option>
        {managers.map((m) => (
          <option key={m.id} value={m.id}>
            {m.business_name || m.email}
          </option>
        ))}
      </select>

      <p className="text-[10px] text-slate-400 mt-2">
        Only partners with type "Property Manager" appear here.{" "}
        <Link to="/admin/partners" className="underline hover:text-cyan-700">
          Manage partners
        </Link>
      </p>
    </div>
  );
}
